<template>
  <b-modal
    ref="modal"
    id="modal-criar-nfe"
    size="lg"
    centered
    hide-header
    hide-footer
    body-class="p-0 overflow-hidden"
    content-class="border-0 shadow-lg"
    @shown="onModalShown()"
  >
    <!-- Custom Premium Header -->
    <div class="modal-header-premium p-3 d-flex align-items-center bg-dark" 
         style="background: linear-gradient(135deg, #20a8d8 0%, #1783ae 100%) !important;">
      <div class="header-icon-wrapper mr-3 bg-white-10">
        <i class="fa fa-plus-circle text-white"></i>
      </div>
      <div>
        <h5 class="mb-0 font-weight-bold text-white">Criar Nova NF-e</h5>
        <small class="text-white opacity-75">Selecione a origem e os dados básicos da nota</small>
      </div>
      <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="hide">
        <i class="fa fa-times fa-lg"></i>
      </b-button>
    </div>

    <div class="p-4">
      <b-form @submit.prevent="onSubmit">
        <!-- Tipo da criação (cards) -->
        <div class="modal-section-title mb-3">
          <i class="fa fa-list mr-2 text-primary"></i> Escolha o Tipo de Emissão
        </div>
        <div class="mb-4">
          <b-row class="mx-n1">
            <b-col md="4" v-for="opt in tipoOptions" :key="opt.value" class="px-1 mb-2">
              <div 
                class="selection-card h-100 p-3 text-center cursor-pointer position-relative"
                :class="{'active': form.tipo === opt.value}"
                @click="form.tipo = opt.value"
              >
                <div class="selection-icon mb-2">
                  <i :class="['fa fa-fw', opt.icon, form.tipo === opt.value ? 'text-primary' : 'text-secondary']"></i>
                </div>
                <div class="selection-title text-dark">{{ opt.text }}</div>
                <small class="selection-desc text-muted d-block lh-1-2">{{ opt.desc }}</small>
                
                <div class="selection-check" v-if="form.tipo === opt.value">
                   <i class="fa fa-check-circle text-primary"></i>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>

        <div class="modal-section-title mb-3">
          <i class="fa fa-building mr-2 text-primary"></i> Dados do Emitente
        </div>
        <!-- Bloco comum -->
        <b-row class="mb-4">
          <b-col md="12">
            <b-form-group label="Empresa Emissora" label-class="modal-label-premium">
              <b-form-select
                id="empresa"
                ref="empresa"
                v-model="form.comum.empresaId"
                :options="empresas.options"
                :state="!!form.comum.empresaId"
                class="rounded-pill shadow-xs"
              >
                <template #first>
                  <option :value="null">Selecione a empresa emissora</option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <div class="modal-section-title mb-3">
          <i class="fa fa-info-circle mr-2 text-primary"></i> Detalhes da Operação
        </div>

        <!-- Fluxo: PRÉ-FATURA -->
        <div v-if="form.tipo === 'PRE_FATURA'" class="premium-card p-3 mb-3 border-primary">
          <b-form-group label="Vincular Pré-fatura" label-class="modal-label-premium">
            <b-input-group>
              <b-form-input
                v-model="form.prefatura.displayText"
                placeholder="Clique na lupa para selecionar a pré-fatura"
                readonly
                class="rounded-left-pill shadow-xs border-right-0"
              />
              <b-input-group-append>
                <b-button variant="primary" @click="abrirLookupPrefatura" class="px-3">
                  <i class="fa fa-search"></i>
                </b-button>
                <b-button 
                  variant="danger" 
                  @click="limparPrefatura"
                  v-if="form.prefatura.id"
                  class="rounded-right-pill px-3"
                >
                  <i class="fa fa-times"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
          <b-alert v-if="!form.comum.empresaId" show variant="warning" class="py-2 mb-0 rounded-lg border-0 shadow-sm">
            <i class="fa fa-exclamation-triangle mr-2"></i> Selecione uma empresa antes de vincular a pré-fatura.
          </b-alert>
        </div>

        <!-- Fluxo: AVULSA (Entrada / Saída) -->
        <div v-if="form.tipo === 'AVULSA'" class="premium-card p-3 mb-3 border-info">
          <b-form-group label="Tipo de Movimentação" label-class="modal-label-premium">
            <b-form-select
              id="comboTIPO_MOVIMENTACAO_ID"
              ref="comboTIPO_MOVIMENTACAO_ID"
              v-model="form.avulsa.tipoMovId"
              :options="tipoMovimentacoes.options"
              class="rounded-pill shadow-xs"
            >
              <template #first><option :value="null">Selecione o Tipo de Movimentação</option></template>
            </b-form-select>
          </b-form-group>

          <b-form-row>
            <b-col md="4">
              <b-form-group label="Sentido" label-class="modal-label-premium">
                <b-form-input
                  v-model="sentidoDisplay"
                  readonly
                  disabled
                  placeholder="Auto"
                  class="rounded-pill bg-light border-0"
                />
              </b-form-group>
            </b-col>

            <b-col md="4">
              <b-form-group label="Finalidade" label-class="modal-label-premium">
                <b-form-input
                  v-model="finalidadeDisplay"
                  readonly
                  disabled
                  placeholder="Auto"
                  class="rounded-pill bg-light border-0"
                />
              </b-form-group>
            </b-col>

            <b-col md="4">
              <b-form-group label="CFOP" label-class="modal-label-premium">
                <b-form-input
                  v-model="cfopDisplay"
                  readonly
                  disabled
                  placeholder="Auto"
                  class="rounded-pill bg-light border-0"
                />
              </b-form-group>
            </b-col>
          </b-form-row>
        </div>

        <!-- Fluxo: COMPRA DE GADO -->
        <div v-if="form.tipo === 'COMPRA_GADO'" class="premium-card p-3 mb-3 border-success">
          <b-form-row>
            <b-col md="8">
              <b-form-group label="Produtor Rural" label-class="modal-label-premium">
                <b-input-group>
                  <b-form-input
                    v-model="form.gado.produtorDisplay"
                    placeholder="Buscar produtor..."
                    readonly
                    class="rounded-left-pill shadow-xs border-right-0"
                  />
                  <b-input-group-append>
                    <b-button variant="success" @click="abrirLookupProdutor" class="rounded-right-pill px-3">
                      <i class="fa fa-search"></i>
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="CFOP" label-class="modal-label-premium">
                <b-form-select v-model="form.gado.cfop" :options="cfops.options" class="rounded-pill shadow-xs">
                  <template #first><option :value="null">Selecione</option></template>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-form-row>
          <b-alert show variant="info" class="py-2 mb-0 rounded-lg border-0 shadow-sm">
            <i class="fa fa-info-circle mr-2"></i> Campos específicos serão completados ao integrar as rotas de compra de gado.
          </b-alert>
        </div>

        <!-- Custom Footer -->
        <div class="modal-footer-premium p-3 bg-light border-top d-flex justify-content-between mx-n4 mb-n4 mt-4">
          <b-button variant="soft-danger" pill @click="hide" class="px-4 font-weight-bold">
            CANCELAR
          </b-button>
          <b-button variant="primary" pill type="submit" :disabled="submitting" class="px-5 font-weight-bold shadow-sm">
            <b-spinner small v-if="submitting" class="mr-1" />
            <i v-else class="fa fa-check-circle mr-1"></i> CRIAR NF-E
          </b-button>
        </div>
      </b-form>
    </div>

    <!-- Modal de Lookup de Pré-fatura -->
    <b-modal
      ref="modalPrefatura"
      id="modal-lookup-prefatura"
      size="xl"
      centered
      hide-header
      hide-footer
      body-class="p-0 overflow-hidden"
      content-class="border-0 shadow-lg"
    >
      <!-- Custom Header for Lookup -->
      <div class="modal-header-premium p-3 d-flex align-items-center bg-dark"
           style="background: linear-gradient(135deg, #343a40 0%, #212529 100%) !important;">
        <div class="header-icon-wrapper mr-3 bg-white-10">
          <i class="fa fa-search text-white"></i>
        </div>
        <div>
          <h5 class="mb-0 font-weight-bold text-white">Localizar Pré-fatura</h5>
          <small class="text-white opacity-75">Filtre e selecione a pré-fatura para faturamento</small>
        </div>
        <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="$refs.modalPrefatura.hide()">
          <i class="fa fa-times fa-lg"></i>
        </b-button>
      </div>

      <div class="p-4">
        <div class="modal-section-title mb-3">
          <i class="fa fa-filter mr-2 text-primary"></i> Filtros de Pesquisa
        </div>
        <b-form @submit.prevent="pesquisarPrefaturas">
          <b-row>
            <b-col md="2">
              <b-form-group label="Data Inicial" label-class="modal-label-premium">
                <CecInputData
                  id="dt1"
                  v-model="lookupPrefatura.filtros.dt1"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label="Data Final" label-class="modal-label-premium">
                <CecInputData
                  id="dt2"
                  v-model="lookupPrefatura.filtros.dt2"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label="N. Pedido" label-class="modal-label-premium">
                <b-form-input
                  id="pedido"
                  v-model="lookupPrefatura.filtros.pedido"
                  inputmode="numeric"
                  class="rounded-pill shadow-xs"
                />
              </b-form-group>
            </b-col>
            
            <b-col md="6">
              <b-form-group label="Cliente" label-class="modal-label-premium">
                <cliente-pesquisa
                  @retornaId="onClientePrefaturaRetornaId"
                  idModal="modalClientePrefatura"
                  id="cliente-prefatura"
                  ref="clientePrefatura"
                  place="Informe o Cliente (F2 para pesquisar)"
                  labelBotao=""
                />
              </b-form-group>
            </b-col>
          </b-row>
          <div class="text-right mb-4">
            <b-button type="submit" variant="primary" pill :disabled="lookupPrefatura.busy" class="px-4 font-weight-bold shadow-sm">
              <i class="fa fa-filter mr-1" v-if="!lookupPrefatura.busy"></i>
              <b-spinner small v-if="lookupPrefatura.busy" class="mr-1" />
              FILTRAR RESULTADOS
            </b-button>
          </div>
        </b-form>

        <div class="modal-section-title mb-3">
          <i class="fa fa-table mr-2 text-primary"></i> Resultados da Busca
        </div>
        <div class="premium-card overflow-hidden border-0 shadow-sm">
          <b-table
            :items="lookupPrefatura.items"
            :fields="lookupPrefatura.fields"
            :busy="lookupPrefatura.busy"
            striped
            hover
            small
            responsive
            show-empty
            empty-text="Nenhuma pré-fatura encontrada"
            class="mb-0"
            @row-dblclick="onPrefaturaDbClick"
          >
            <template #cell(DT_SAIDA)="data">
              <span class="font-weight-bold text-dark">{{ formatarData(data.value) }}</span>
            </template>
            <template #cell(VL_TOTAL)="data">
              <span class="text-success font-weight-bold">{{ formatarMoeda(data.value) }}</span>
            </template>
            <template #cell(actions)="data">
              <b-button 
                size="sm" 
                variant="primary"
                class="rounded-pill px-3"
                @click="selecionarPrefatura(data.item)"
                :disabled="submitting"
              >
                <i class="fa fa-check mr-1"></i> Selecionar
              </b-button>
            </template>
            <template #table-busy>
              <div class="text-center text-primary my-4">
                <b-spinner class="align-middle mr-2"></b-spinner>
                <strong>Buscando pré-faturas...</strong>
              </div>
            </template>
          </b-table>
        </div>
      </div>

      <div class="modal-footer-premium p-3 bg-light border-top d-flex justify-content-end">
        <b-button variant="soft-danger" pill @click="$refs.modalPrefatura.hide()" class="px-4 font-weight-bold">
          FECHAR
        </b-button>
      </div>
    </b-modal>
  </b-modal>
</template>

<script>
import Tools from '@/tools/tools.js'
import CecInputData from '@/components/Cec/CecInputData.vue'

export default {
  name: 'CriarNfeModal',
  components: {
    CecInputData,
    ClientePesquisa: () => import('@/views/pesquisa/ClientePesquisa.vue')
  },
  data () {
    return {
      submitting: false,
      tipoOptions: [
        { 
          value: 'PRE_FATURA',  
          text: 'Vincular Pré-fatura', 
          desc: 'Importar dados de uma venda prévia',
          icon: 'fa-file-invoice-dollar'
        },
        { 
          value: 'AVULSA',      
          text: 'Emissão Avulsa', 
          desc: 'Entrada ou Saída manual',
          icon: 'fa-edit'
        },
        { 
          value: 'COMPRA_GADO', 
          text: 'Compra de Gado', 
          desc: 'Fluxo para gado de corte',
          icon: 'fa-paw'
        }
      ],
      sentidoOptions: [
        { value: 'SAIDA',   text: 'Saída' },
        { value: 'ENTRADA', text: 'Entrada' }
      ],
      finalidadeOptions: [
        { value: '1', text: '1 - NF-e normal' },
        { value: '2', text: '2 - NF-e complementar' },
        { value: '3', text: '3 - NF-e de ajuste' },
        { value: '4', text: '4 - Devolução' }
      ],
      empresas: { items: [], options: [] },
      tipoMovimentacoes: { items: [], options: [] },
      tipoMovMap: new Map(), // ID -> objeto do backend (com FORMA, OP_FINALIDADE_EMISSAO, CFOP_ID)
      cfops: { items: [], options: [] },
      form: {
        tipo: 'AVULSA', // default: vamos priorizar a Avulsa
        comum: { empresaId: null },
        prefatura: { 
          id: null,
          displayText: ''
        },
        avulsa: {
          sentido: null,      // S | E
          finalidade: '1',    // 1..4
          tipoMovId: null,    // ID do tipo de movimentação
          cfopId: null        // CONTADOR do CFOP
        },
        gado: {
          produtorId: null,
          produtorDisplay: '',
          cfop: null
        }
      },
      lookupPrefatura: {
        busy: false,
        items: [],
        filtros: {
          dt1: null,
          dt2: null,
          id: null,
          pedido: null,
          nf: null,
          forma: null,
          clienteId: null
        },
        fields: [
          { key: 'SEQUENCIA', label: 'N. Controle', sortable: true },
          { key: 'N_VENDA', label: 'Pedido', sortable: true },
          { key: 'CODIGO', label: 'Código', sortable: true },
          { key: 'RAZAOSOCIAL', label: 'Cliente - Razão Social', sortable: true },
          { key: 'NOME', label: 'Fantasia', sortable: true },
          { key: 'DT_SAIDA', label: 'Dt. Venda', sortable: true },
          { key: 'FORMA', label: 'Forma', sortable: true },
          { key: 'NUMNF', label: 'Nota Fiscal', sortable: true },
          { key: 'VL_TOTAL', label: 'Total (R$)', sortable: true, class: 'text-right' },
          { key: 'actions', label: 'Ações', class: 'text-center' }
        ]
      },
      formaOptions: [
        { value: 'V', text: 'V - Vista' },
        { value: 'P', text: 'P - Prazo' }
      ]
    }
  },
  computed: {
    sentidoDisplay() {
      const s = this.form.avulsa.sentido
      if (!s) return ''
      return s === 'N' ? 'Entrada' : s === 'S' ? 'Saída' : s
    },
    finalidadeDisplay() {
      const f = this.form.avulsa.finalidade
      if (!f) return ''
      const opt = this.finalidadeOptions.find(o => String(o.value) === String(f))
      return opt ? opt.text : f
    },
    cfopDisplay() {
      const c = this.form.avulsa.cfopId
      if (!c) return ''
      const opt = this.cfops.options.find(o => String(o.value) === String(c))
      return opt ? opt.text : c
    }
  },
  created () {
    this.carregarEmpresas()
    this.carregarTipoMovimentacoes()
    this.carregarCfops()
    this.inicializarDatasPrefatura()
  },
  watch: {
    // Quando o usuário trocar o Tipo de Movimentação, ajusta Sentido/Finalidade/CFOP
    'form.avulsa.tipoMovId'(novo) { this.onTipoMovChange(novo) }
  },
  methods: {
    show () { this.$refs.modal.show() },
    hide () { this.$refs.modal.hide() },

    onModalShown () {
      this.carregarTipoMovimentacoes()
      if (this.$refs.empresa) {
        this.$refs.empresa.focus()
      }
    },

    async onSubmit () {
      if (!this.validar()) return
      this.submitting = true
      try {
        const payload = this.montarPayload()
        this.$emit('create', payload)
        this.hide()
      } finally {
        this.submitting = false
      }
    },

    montarPayload () {
      const t = this.form.tipo
      const base = { tipo: t, empresaId: this.form.comum.empresaId }

      if (t === 'PRE_FATURA') {
        return { ...base, prefaturaId: this.form.prefatura.id }
      }
      if (t === 'AVULSA') {
        const { sentido, finalidade, tipoMovId, cfopId } = this.form.avulsa
        return { ...base, sentido, finalidade, tipoMovId, cfopId }
      }
      if (t === 'COMPRA_GADO') {
        const { produtorId, cfop } = this.form.gado
        return { ...base, produtorId, cfop }
      }
      return base
    },

    validar () {
      if (!this.form.comum.empresaId) {
        this.toast('Selecione a empresa.', 'warning'); return false
      }
      if (this.form.tipo === 'PRE_FATURA') {
        if (!this.form.prefatura.id) { this.toast('Informe a pré-fatura.', 'warning'); return false }
      }
      if (this.form.tipo === 'AVULSA') {
        const a = this.form.avulsa
        if (!a.sentido)   { this.toast('Selecione Entrada/Saída.', 'warning'); return false }
        if (!a.tipoMovId) { this.toast('Selecione o Tipo de Movimentação.', 'warning'); return false }
        if (!a.cfopId)    { this.toast('Selecione o CFOP.', 'warning'); return false }
      }
      if (this.form.tipo === 'COMPRA_GADO') {
        if (!this.form.gado.produtorId) { this.toast('Selecione o produtor.', 'warning'); return false }
        if (!this.form.gado.cfop)       { this.toast('Selecione o CFOP.', 'warning'); return false }
      }
      return true
    },

    toast (msg, variant = 'info') {
      this.$bvToast && this.$bvToast.toast(msg, {
        title: 'Criar NF-e',
        variant,
        autoHideDelay: 2500,
        solid: true
      })
    },

    inicializarDatasPrefatura() {
      const hoje = new Date()
      const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1)
      this.lookupPrefatura.filtros.dt1 = this.formatarDataISO(primeiroDiaMes)
      this.lookupPrefatura.filtros.dt2 = this.formatarDataISO(hoje)
    },

    abrirLookupPrefatura () { 
      if (!this.form.comum.empresaId) {
        this.toast('Selecione uma empresa antes de pesquisar pré-faturas.', 'warning')
        return
      }
      this.$refs.modalPrefatura.show()
      // Auto-pesquisar ao abrir
      this.$nextTick(() => {
        this.pesquisarPrefaturas()
      })
    },

    async pesquisarPrefaturas() {
      try {
        this.lookupPrefatura.busy = true
        this.lookupPrefatura.items = []

        const params = {
          dt1: this.lookupPrefatura.filtros.dt1,
          dt2: this.lookupPrefatura.filtros.dt2
        }

        if (this.lookupPrefatura.filtros.id) {
          params.id = this.lookupPrefatura.filtros.id
        }
        if (this.lookupPrefatura.filtros.pedido) {
          params.pedido = this.lookupPrefatura.filtros.pedido
        }
        if (this.lookupPrefatura.filtros.nf) {
          params.nf = this.lookupPrefatura.filtros.nf
        }
        if (this.lookupPrefatura.filtros.forma) {
          params.forma = this.lookupPrefatura.filtros.forma
        }
        if (this.lookupPrefatura.filtros.clienteId) {
          params.clienteId = this.lookupPrefatura.filtros.clienteId
        }

        const { data } = await this.$http.get('/api/protected/venda/pesquisarVendasNF', { params })
        this.lookupPrefatura.items = Array.isArray(data) ? data : []

        if (this.lookupPrefatura.items.length === 0) {
          this.toast('Nenhuma pré-fatura encontrada com os filtros informados.', 'info')
        }
      } catch (err) {
        console.error('Erro ao pesquisar pré-faturas:', err)
        const msg = err?.response?.data || err?.message || 'Erro ao pesquisar pré-faturas'
        this.toast(msg, 'danger')
      } finally {
        this.lookupPrefatura.busy = false
      }
    },

    onPrefaturaDbClick(item, index, event) {
      this.selecionarPrefatura(item)
    },

    async selecionarPrefatura(prefatura) {
      if (!prefatura) return
      
      this.form.prefatura.id = prefatura.SEQUENCIA
      this.form.prefatura.displayText = `${prefatura.N_VENDA} - ${prefatura.RAZAOSOCIAL}`
      
      // Iniciar criação da NF-e imediatamente
      await this.criarNfePrefatura(prefatura.SEQUENCIA)
    },

    async criarNfePrefatura(vendaId) {
      try {
        this.submitting = true
        
        // Validar empresa selecionada
        if (!this.form.comum.empresaId) {
          this.toast('Selecione uma empresa antes de criar a NF-e.', 'warning')
          return
        }

        
        const payload = {
          vendaId,
          empresaId: this.form.comum.empresaId,
          usuario: window.localStorage.getItem('login')
        }

        this.toast('Criando NF-e a partir da pré-fatura...', 'info')

        const { data } = await this.$http.post('/api/protected/notaFiscal/iniciarNfeVenda', payload)
        
        const nfeId = data?.nfeId || data?.ID || (Array.isArray(data) ? data[0]?.ID : null)
        
        if (!nfeId) {
          throw new Error('ID da NF-e não retornado pelo backend')
        }

        this.toast('NF-e criada com sucesso!', 'success')
        
        // Fechar modais
        this.$refs.modalPrefatura.hide()
        this.hide()
        
        // Redirecionar para edição da NF-e
        this.$router.push({ name: 'NFe - Cadastro', params: { id: nfeId } })
        
      } catch (err) {
        console.error('Erro ao criar NF-e a partir da pré-fatura:', err)
        Tools.exibirMensagem(this.$store, 'Aviso', 'danger', err )
      } finally {
        this.submitting = false
      }
    },

    obterTipoMovimentacaoVenda() {
      // Buscar tipo de movimentação que seja de VENDA/SAÍDA
      const tipoVenda = this.tipoMovimentacoes.items.find(t => {
        const forma = String(t.FORMA || '').toUpperCase().trim()
        return forma === 'VENDA' || forma === 'SAIDA' || forma === 'SAÍDA' || forma === 'S'
      })
      
      if (tipoVenda) {
        return tipoVenda.ID
      }
      
      // Fallback: usar o primeiro disponível
      return this.tipoMovimentacoes.options[0]?.value || null
    },

    limparPrefatura() {
      this.form.prefatura.id = null
      this.form.prefatura.displayText = ''
    },

    onClientePrefaturaRetornaId(clienteId) {
      this.lookupPrefatura.filtros.clienteId = clienteId
    },

    abrirLookupProdutor ()  { this.toast('Lookup de produtor ainda não implementado.', 'info') },

    // ==================== LOADERS ====================
    async carregarEmpresas () {
      try {
        this.empresas.items = []
        this.empresas.options = []
        const { data } = await this.$http.get(
          this.$http.defaults.baseURL.concat('api/protected/empresa/getTodas')
        )
        for (const emp of (data || [])) {
          this.empresas.options.push({
            value: emp.CODIGO,
            text: `${emp.APELIDO} - ${emp.IE} - ${emp.RAZAO}`
          })
        }
      } catch (err) { console.error('carregarEmpresas:', err) }
    },

    async carregarTipoMovimentacoes () {
      try {
        // Backend: SELECT ID, DESCRICAO, TRADUCAO, CFOP_ID, ... FORMA, OP_FINALIDADE_EMISSAO ...
        const { data } = await this.$http.get(`/api/protected/tipoMovimentacao/listar`)
        const lista = Array.isArray(data) ? data : []

        this.tipoMovimentacoes.items = lista
        this.tipoMovimentacoes.options = lista.map(t => ({
          value: t.ID,
          text:  t.TRADUCAO || `${t.ID} - ${t.DESCRICAO}`
        }))

        this.tipoMovMap.clear()
        for (const t of lista) this.tipoMovMap.set(String(t.ID), t)
      } catch (err) {
        console.error('carregarTipoMovimentacoes:', err)
      }
    },

    async carregarCfops () {
      try {
        this.cfops.items = []
        this.cfops.options = []
        const { data } = await this.$http.get(`/api/protected/cfop/listarTodos`)
        for (const c of (Array.isArray(data) ? data : [])) {
          this.cfops.items.push(c)
          this.cfops.options.push({ 
            value: c.CONTADOR,
            text: `${c.CONTADOR} - ${c.NATUREZA}`
          })
        }
      } catch (err) { console.error('carregarCfops:', err) }
    },

    // ====== Auto-preenchimento baseado no Tipo de Movimentação ======
    onTipoMovChange (id) {
      const mov = this.tipoMovMap.get(String(id))
      if (!mov) return

      // 1) Sentido a partir de OP_TIPO_MOVIMENTACAO: retorna S ou E
      const sentido = mov.OP_TIPO_MOVIMENTACAO === 'S' ? 'S' : mov.OP_TIPO_MOVIMENTACAO === 'N' ? 'N' : null
      if (sentido) this.form.avulsa.sentido = sentido

      // 2) Finalidade a partir de OP_FINALIDADE_EMISSAO (1..4)
      const fin = this.normalizaFinalidade(mov.OP_FINALIDADE_EMISSAO)
      if (fin) this.form.avulsa.finalidade = fin

      // 3) CFOP: buscar pelo CONTADOR (PK)
      if (mov.CFOP_ID != null) {
        const cfopObj = this.cfops.items.find(c => String(c.CONTADOR) === String(mov.CFOP_ID))
        if (cfopObj) {
          this.form.avulsa.cfopId = cfopObj.CONTADOR
        } else {
          this.form.avulsa.cfopId = null
        }
      }
    },

    normalizaSentido (v) {
      if (!v) return null
      const s = String(v).toUpperCase().trim()
      
      // Mapeia FORMA (VENDA, COMPRA, TROCA, etc.) para S ou E
      if (s === 'COMPRA') return 'E'
      if (s === 'VENDA' || s === 'TROCA' || s === 'DEVOLUCAO' || s === 'DEVOLUÇÃO') return 'S'
      
      // Fallback: aceita E/S/ENTRADA/SAIDA direto (caso backend mude)
      if (s === 'E' || s === 'ENTRADA') return 'E'
      if (s === 'S' || s === 'SAIDA' || s === 'SAÍDA') return 'S'
      
      return null
    },

    normalizaFinalidade (v) {
      if (v == null) return null
      const f = String(v).trim()
      if (/^[1-4]$/.test(f)) return f
      return null
    },

    // Helpers de formatação
    formatarDataISO(date) {
      if (!date) return ''
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    formatarData(dateStr) {
      return Tools.formatarDataBR(dateStr)
    },

    formatarMoeda(value) {
      if (value == null) return 'R$ 0,00'
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value)
    }
  }
}
</script>

<style scoped>
.modal-header-premium {
    border-bottom: none;
}

.header-icon-wrapper {
    width: 45px;
    height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    font-size: 1.2rem;
}

.bg-white-10 {
    background-color: rgba(255, 255, 255, 0.15) !important;
}

.modal-section-title {
    font-size: 0.85rem;
    font-weight: 700;
    color: #2d3748;
    border-bottom: 2px solid #edf2f7;
    padding-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.modal-label-premium {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6c757d;
    margin-bottom: 0.4rem;
    display: block;
}

.shadow-xs {
    box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important;
}

.btn-soft-danger {
    background-color: rgba(248, 108, 107, 0.1);
    color: #f86c6b;
    border: none;
}
.btn-soft-danger:hover {
    background-color: #f86c6b;
    color: white;
}

.nav-pills-premium >>> .btn {
  border-radius: 50px !important;
  margin: 0 4px;
  font-weight: 600;
  font-size: 0.9rem;
}

.premium-card {
  background: #fff;
  border-radius: 15px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  transition: all 0.3s ease;
}

.premium-card:hover {
  box-shadow: 0 8px 24px rgba(0,0,0,0.08);
}

.selection-card {
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}

.selection-card:hover {
  border-color: #cbd5e1;
  background: #f1f5f9;
}

.selection-card.active {
  background: #fff;
  border-color: #20a8d8;
  box-shadow: 0 4px 12px rgba(32, 168, 216, 0.15);
}

.selection-icon {
  font-size: 1.5rem;
}

.selection-title {
  font-size: 0.9rem;
  font-weight: 700;
  margin-bottom: 2px;
}

.selection-desc {
  font-size: 0.75rem;
  line-height: 1.1;
}

.selection-check {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 1.1rem;
}

.lh-1-2 {
  line-height: 1.2;
}
</style>



// WEBPACK FOOTER //
// src/views/faturamento/nfe/partials/CriarNfeModal.vue